import React from 'react';
import './ErrorBoundary.css';

/**
 * ErrorBoundary — the last surface, and it wears the house voice.
 *
 * A centred card apologising in a small serif was the one screen in the app
 * that looked like somebody else's product. This is the brutalist register the
 * rest of the field speaks: full bleed, left-aligned, the statement loud and
 * the facts beneath it in small grey mono — the same shape a missions tile or
 * a hub leaf takes.
 *
 * It also says something true. A blank apology tells you nothing; the error's
 * own message is the one fact worth carrying, so it stands in the ledger where
 * anyone can read it back to us.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the stack where a developer will actually find it: the console.
    // (A monitoring transport belongs here when there is one.)
    console.error('[nucleus] uncaught error', error, errorInfo);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || String(this.state.error || 'Unknown error');

    return (
      <div className="errpage">
        <div className="errpage__body">
          <h1 className="errpage__title">
            Something<br />went wrong
          </h1>

          <div className="errpage__ledger">
            <div className="errpage__fact">
              <span className="errpage__label">What</span>
              <span className="errpage__value">{message}</span>
            </div>
            <div className="errpage__fact">
              <span className="errpage__label">Where</span>
              <span className="errpage__value">{window.location.pathname}</span>
            </div>
          </div>
        </div>

        {/* The way out is a full-width cell at the foot — the same flush block
            the hub's leaves are, not a pill floating in whitespace. */}
        <div className="errpage__doors">
          <button type="button" className="btn errpage__door" onClick={() => window.location.reload()}>
            <span className="errpage__door-name">Reload</span>
            <span className="errpage__door-hint">try this page again</span>
          </button>
          <button type="button" className="btn errpage__door" onClick={() => { window.location.href = '/graph'; }}>
            <span className="errpage__door-name">Go to the graph</span>
            <span className="errpage__door-hint">leave this page behind</span>
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
